import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoGrid, IoTrendingUp, IoArrowForward } from 'react-icons/io5';
import { type Category } from '@/hooks/useProducts';
import Button from '@/components/common/Button/Button';
import { sampleProductImage } from '@/utils/constants/common';
import './CategoryCard.css';

interface CategoryCardProps {
    category: Category;
    variant?: 'default' | 'featured' | 'simple';
    showStats?: boolean;
    showOverlay?: boolean;
    animationDelay?: number;
    onClick?: (categoryId: number) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
    category,
    variant = 'default',
    showStats = true,
    showOverlay = true,
    animationDelay = 0,
    onClick
}) => {
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);

    const handleCategoryClick = () => {
        if (onClick) {
            onClick(category.id);
        } else {
            navigate(`/products?category=${category.id}`);
        }
    };

    const cardClassName = `category_card category_card_${variant} ${isHovered ? 'hovered' : ''}`;
    const cardStyle = animationDelay > 0 ? { '--animation-delay': `${animationDelay}s` } as React.CSSProperties : {};

    // Generate random product count for display
    const productCount = Math.floor(Math.random() * 50) + 10;

    return (
        <div 
            className={cardClassName}
            style={cardStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleCategoryClick}
        >
            <div className="category_image_container">
                <div className="category_icon">
                    {variant === 'simple' ? <IoTrendingUp size={32} /> : <IoGrid size={32} />}
                </div>
                <img
                    src={category.image_url || sampleProductImage}
                    alt={category.name}
                    className="category_image"
                />
                
                {showOverlay && (
                    <div className="category_overlay">
                        {variant === 'simple' ? (
                            <span>Explore</span>
                        ) : (
                            <div className="overlay_content">
                                <h3>{category.name}</h3>
                                <Button variant="primary" size="sm">
                                    Explore <IoArrowForward size={14} />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div className="category_content">
                <h3 className="category_name">{category.name}</h3>
                <p className="category_description">
                    {category.description || `Explore our ${category.name.toLowerCase()} collection`}
                </p>
                
                {showStats && (
                    <div className="category_stats">
                        {variant === 'default' && (
                            <div className="stat_badge">
                                <IoTrendingUp size={14} />
                                <span>Popular</span>
                            </div>
                        )}
                        <div className="product_count">
                            {productCount}+ items
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoryCard;
